import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { lastActiveText } from "@/features/entities/last-active-display";
import { useDeleteGroup } from "@/hooks/use-data";
import type { TargetEnv, UnifiedGroupRecord } from "@/lib/types";
import { useMemo, useState } from "react";
import { TargetEnvRadio } from "./target-env-radio";

interface Props {
  group: UnifiedGroupRecord;
  onClose: () => void;
  onJobEnqueued: (
    jobIds: { prod: string | null; dev: string | null },
    group: UnifiedGroupRecord,
  ) => void;
}

function fmt(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function envCopy(env: TargetEnv | null): string {
  if (env === null) return "Pick a target environment to enable Delete.";
  if (env === "both")
    return "Soft-deletes the group from BOTH prod and dev. Cascades memberships, GAD, links, sub-classifications, and risk-score recompute on each env. Items + messages are swept asynchronously per env.";
  const label = env === "prod" ? "production" : "development";
  return `Soft-deletes the group from ${label} only. Cascades memberships, GAD, links, sub-classifications, and risk-score recompute on that env. Items + messages are swept asynchronously.`;
}

export function GroupDeleteDialog({ group, onClose, onJobEnqueued }: Props) {
  const [typed, setTyped] = useState("");
  const [targetEnv, setTargetEnv] = useState<TargetEnv | null>(null);
  const mutation = useDeleteGroup();
  const errorMessage = mutation.error?.message ?? null;

  const confirmEnabled = useMemo(() => {
    if (targetEnv === null) return false;
    if (typed === "DELETE") return true;
    if (group.scrapping_id && typed === group.scrapping_id) return true;
    return false;
  }, [typed, group.scrapping_id, targetEnv]);

  function handleConfirm() {
    if (targetEnv === null) return;
    mutation.mutate(
      { id: group.id, targetEnv },
      {
        onSuccess: (data) => {
          onJobEnqueued(data.items_job_ids, group);
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        <div>
          <DialogTitle>Delete group</DialogTitle>
          <DialogDescription>{envCopy(targetEnv)}</DialogDescription>
        </div>

        <TargetEnvRadio value={targetEnv} onChange={setTargetEnv} actionVerb="delete" />

        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            rowGap: 6,
            columnGap: 12,
            fontSize: 12,
          }}
        >
          <dt style={{ color: "var(--iris-fg-4)" }}>Name</dt>
          <dd style={{ color: "var(--iris-fg-1)", fontWeight: 500 }}>{fmt(group.display_name)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Encrypted ID</dt>
          <dd style={{ color: "var(--iris-fg-2)", fontFamily: "var(--iris-font-mono)" }}>
            {fmt(group.id)}
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>External ID</dt>
          <dd style={{ color: "var(--iris-fg-2)", fontFamily: "var(--iris-font-mono)" }}>
            {fmt(group.scrapping_id)}
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Platform</dt>
          <dd>{fmt(group.group_application_type)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Prod / Dev status</dt>
          <dd>
            {fmt(group.prod_status)} / {fmt(group.dev_status)}
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Members</dt>
          <dd>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 8,
                rowGap: 2,
                fontSize: 12,
              }}
            >
              <span style={{ color: "var(--iris-fg-4)" }}>prod</span>
              <span>
                {"—"}{" "}
                <span style={{ color: "var(--iris-fg-5)", fontSize: 11 }}>
                  (live count pulled by backend)
                </span>
              </span>
              <span style={{ color: "var(--iris-fg-4)" }}>dev</span>
              <span>{fmt(group.dev_members)}</span>
            </div>
          </dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Risk</dt>
          <dd>{fmt(group.risk_score)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Classification</dt>
          <dd>{fmt(group.classification)}</dd>

          <dt style={{ color: "var(--iris-fg-4)" }}>Last active</dt>
          <dd>{lastActiveText(group.last_active_at, fmtDate)}</dd>
        </dl>

        <div>
          <label
            htmlFor="ir661-confirm"
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--iris-fg-3)",
              marginBottom: 6,
            }}
          >
            Type <strong>DELETE</strong>
            {group.scrapping_id ? (
              <>
                {" "}
                or <strong>{group.scrapping_id}</strong>
              </>
            ) : null}{" "}
            to confirm.
          </label>
          <input
            id="ir661-confirm"
            className="iris-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type DELETE to confirm"
            autoComplete="off"
            style={{ width: "100%" }}
          />
        </div>

        {errorMessage ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--iris-neon-pink)",
              padding: "8px 10px",
              border: "1px solid var(--iris-neon-pink)",
              borderRadius: 6,
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: "auto",
          }}
        >
          <button
            type="button"
            className="iris-btn"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            onClick={handleConfirm}
            disabled={!confirmEnabled || mutation.isPending}
          >
            {mutation.isPending ? "Deleting…" : "Delete group"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
